
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Input,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
} from '@fluentui/react-components';
import { Edit24Regular, Open24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ProjektPartnerItem } from '../../models/ProjektPartnerItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';
import ProjektpartnerDrawer from './ProjektpartnerDrawer';

const EXCEL_COLUMNS: ExcelExportColumn<ProjektPartnerItem>[] = [
    { header: 'Company/Institution', value: (p) => p.bezeichnung1, width: 50 },
    { header: 'Postal Code', value: (p) => p.plz, width: 12 },
    { header: 'City', value: (p) => p.ort, width: 25 },
    { header: 'Country', value: (p) => p.nation },
    { header: 'Financial Partner Code', value: (p) => p.finanzPartnerCodeBezeichnung, width: 30 },
    { header: 'Cooperation Partner Code', value: (p) => p.koopPartnerCodeBezeichnung, width: 30 },
    { header: 'Contracts', value: (p) => p.anzahlVertraege ?? 0, width: 12 },
    { header: 'Projects', value: (p) => p.anzahlProjekte ?? 0, width: 12 },
    { header: 'Funding Agency', value: (p) => (p.isFoerdergeber ? 'Yes' : 'No'), width: 15 },
];

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = keyof ProjektPartnerItem | null;

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    searchInput: {
        width: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    colActions: {
        width: '80px',
        minWidth: '80px',
    },
    colCompany: {
        width: '35%',
        minWidth: '250px',
    },
    colPostalCode: {
        width: '100px',
        minWidth: '100px',
    },
    colCity: {
        width: '150px',
        minWidth: '150px',
    },
    colContracts: {
        width: '90px',
        minWidth: '90px',
    },
    colProjects: {
        width: '90px',
        minWidth: '90px',
    },
    colFunding: {
        width: '110px',
        minWidth: '110px',
    },
});

const Projektpartnerverwaltung: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [partners, setPartners] = useState<ProjektPartnerItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn>('bezeichnung1');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerPartnerId, setDrawerPartnerId] = useState<number | null>(null);
    const [drawerIsEditMode, setDrawerIsEditMode] = useState(false);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all partners
    useEffect(() => {
        const fetchPartners = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const allPartners = await dataService.getProjektpartnerListeAllCompanies();
                setPartners(allPartners || []);
            } catch (err) {
                console.error('Error loading partners:', err);
                setPartners([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPartners();
    }, [dataService]);

    // Filter partners based on search text
    const filteredPartners = useMemo(() => {
        let results = partners;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((partner) => {
                return (
                    partner.bezeichnung1?.toLowerCase().includes(searchLower) ||
                    partner.plz?.toLowerCase().includes(searchLower) ||
                    partner.ort?.toLowerCase().includes(searchLower) ||
                    partner.nation?.toLowerCase().includes(searchLower) ||
                    partner.finanzPartnerCodeBezeichnung?.toLowerCase().includes(searchLower) ||
                    partner.koopPartnerCodeBezeichnung?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply sorting
        if (sortColumn) {
            results = [...results].sort((a, b) => {

                let aValue: any = a[sortColumn];

                let bValue: any = b[sortColumn];

                // Handle null/undefined
                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                // Convert to lowercase for string comparison
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Comparison
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return results;
    }, [partners, filterText, sortColumn, sortDirection]);

    const handleView = (partner: ProjektPartnerItem) => {
        setDrawerPartnerId(partner.partnerID);
        setDrawerIsEditMode(false);
        setDrawerOpen(true);
    };

    const handleEdit = (partner: ProjektPartnerItem) => {
        setDrawerPartnerId(partner.partnerID);
        setDrawerIsEditMode(true);
        setDrawerOpen(true);
    };

    const handleRowDoubleClick = (partner: ProjektPartnerItem) => {
        handleView(partner);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setDrawerPartnerId(null);
        setDrawerIsEditMode(false);
    };

    const handleDrawerSave = (updatedPartner: ProjektPartnerItem) => {
        // Update the partner in the list
        setPartners((prevPartners) =>
            prevPartners.map((p) =>
                p.partnerID === updatedPartner.partnerID ? updatedPartner : p
            )
        );
    };

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction or clear sort
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn('bezeichnung1');
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    return (
        <div className={styles.container}>
            {/* Filter Input */}
            <div className={styles.filterContainer}>
                <Input
                    placeholder="Search by company, postal code, city, country, or code..."
                    value={filterText}
                    onChange={(_, data) => setFilterText(data.value)}
                    className={styles.searchInput}
                />
                <ExcelExportButton
                    items={filteredPartners}
                    columns={EXCEL_COLUMNS}
                    fileName={`ProjectPartners_${new Date().toISOString().split('T')[0]}`}
                    sheetName="Project Partners"
                />
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading project partners..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && partners.length === 0 && (
                <div className={styles.emptyState}>
                    No project partners found
                </div>
            )}

            {/* Partners Table */}
            {!isLoading && filteredPartners.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table
                        size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions} onClick={() => handleSort('partnerID')}>
                                    Actions
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colCompany} onClick={() => handleSort('bezeichnung1')}>
                                    Company/Institution
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colPostalCode} onClick={() => handleSort('plz')}>
                                    Postal Code
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colCity} onClick={() => handleSort('ort')}>
                                    City
                                </TableHeaderCell>
                                <TableHeaderCell onClick={() => handleSort('nation')}>
                                    Country
                                </TableHeaderCell>
                                <TableHeaderCell onClick={() => handleSort('finanzPartnerCodeBezeichnung')}>
                                    Financial Partner Code
                                </TableHeaderCell>
                                <TableHeaderCell onClick={() => handleSort('koopPartnerCodeBezeichnung')}>
                                    Cooperation Partner Code
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colContracts} onClick={() => handleSort('anzahlVertraege')}>
                                    Contracts
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colProjects} onClick={() => handleSort('anzahlProjekte')}>
                                    Projects
                                </TableHeaderCell>
                                <TableHeaderCell className={styles.colFunding} onClick={() => handleSort('isFoerdergeber')}>
                                    Funding Agency
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredPartners.map((partner) => (
                                <TableRow
                                    key={partner.partnerID}
                                    className={styles.tableRow}
                                    onDoubleClick={() => handleRowDoubleClick(partner)}
                                >
                                    <TableCell>
                                        <TableCellLayout>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    icon={<Open24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleView(partner);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="View"
                                                />
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(partner);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Edit"
                                                />
                                            </div>
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.bezeichnung1 || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.plz || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.ort || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.nation || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.finanzPartnerCodeBezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.koopPartnerCodeBezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.anzahlVertraege || '0'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.anzahlProjekte || '0'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{partner.isFoerdergeber ? 'Yes' : 'No'}</TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && partners.length > 0 && filteredPartners.length === 0 && (
                <div className={styles.emptyState}>
                    No project partners match your search
                </div>
            )}

            {/* Partner Drawer */}
            <ProjektpartnerDrawer
                isOpen={drawerOpen}
                partnerId={drawerPartnerId}
                isEditMode={drawerIsEditMode}
                onClose={handleDrawerClose}
                onSave={handleDrawerSave}
            />
        </div>
    );
};

export default Projektpartnerverwaltung;
